import { useCallback } from 'react';
import { Navigate } from 'react-router-dom';

import { useShowGraphqlUserVisibleErrorSnackbar } from '@ocx-app/hooks/use-show-graphql-user-visible-error-snackbar.hook';
import { ScreenLoader } from '@ocx/ui';
import {
  applePayPaymentInstrumentPlaceholder,
  googlePayPaymentInstrumentPlaceholder,
} from '@ocx/data-payment-instruments';
import { useShowGenericErrorSnackbar } from '@ocx-app/hooks/useShowGenericErrorSnackbar';
import { useGetNavigationActions } from '@ocx-app/hooks/useGetNavigationActions';
import { AppRoutePath } from '@ocx-app/pages/AppRoutePath';

import { SelectPump } from './select-pump.component';
import { SelectPumpLayout } from './select-pump.layout';
import { useSelectPumpController } from './hooks/use-select-pump-controller.hook';
import { useHandleAddPaymentMethodClick } from './hooks/use-handle-add-payment-method-click.hook';

export const SelectPumpController = () => {
  const showGenericErrorSnackbar = useShowGenericErrorSnackbar();
  const showGraphqlUserVisibleErrorSnackbar = useShowGraphqlUserVisibleErrorSnackbar();

  const actions = useGetNavigationActions();
  const onSiteLoadFailed = () => {
    showGenericErrorSnackbar();
    actions.pushToHome();
  };

  const {
    loading,
    store,
    selectPump,
    deselectPump,
    selectedPump,
    shouldPrintReceipt,
    togglePrintReceipt,
    handlePurchase,
    carWashListController,
    paymentInstrumentsListSelector,
    isPaymentInstrumentsLoading,
    isCardsAvailable,
    isWalletSizeLimitReached,
  } = useSelectPumpController({
    onSiteLoadFailed,
    onError: showGraphqlUserVisibleErrorSnackbar,
  });

  const handleAddPaymentMethodClick = useHandleAddPaymentMethodClick({
    isWalletSizeLimitReached,
    isGoToWalletButtonShown: true,
  });

  const handlePurchaseGooglePay = useCallback(
    () => handlePurchase(googlePayPaymentInstrumentPlaceholder.uuid),
    [handlePurchase],
  );
  const handlePurchaseApplePay = useCallback(
    () => handlePurchase(applePayPaymentInstrumentPlaceholder.uuid),
    [handlePurchase],
  );
  const handlePurchaseCard = useCallback(() => {
    if (paymentInstrumentsListSelector.selectedListItem) {
      handlePurchase(paymentInstrumentsListSelector.selectedListItem.uuid);
    }
  }, [handlePurchase, paymentInstrumentsListSelector.selectedListItem]);

  if (loading && !store) {
    return (
      <SelectPumpLayout>
        <ScreenLoader />
      </SelectPumpLayout>
    );
  }

  if (!store) {
    return <Navigate to={AppRoutePath.Home} replace />;
  }

  return (
    <SelectPumpLayout>
      <SelectPump
        addressLine1={store.address.addressLine1}
        addressLine2={store.address.addressLine2}
        pumps={store.pumps}
        carWashListController={carWashListController}
        paymentInstrumentsListSelector={paymentInstrumentsListSelector}
        isPaymentInstrumentsLoading={isPaymentInstrumentsLoading}
        selectPump={selectPump}
        deselectPump={deselectPump}
        selectedPump={selectedPump}
        shouldPrintReceipt={shouldPrintReceipt}
        togglePrintReceipt={togglePrintReceipt}
        handlePurchaseGooglePay={handlePurchaseGooglePay}
        handlePurchaseApplePay={handlePurchaseApplePay}
        onAddPaymentMethodClick={handleAddPaymentMethodClick}
        handlePurchaseCard={handlePurchaseCard}
        isCardsAvailable={isCardsAvailable}
      />
    </SelectPumpLayout>
  );
};
